import { Fragment, useEffect, useMemo, useState } from "react";
import {
  Loader2,
  Search,
  Download,
  ChevronDown,
  ShieldCheck,
  RefreshCw,
  Smartphone,
  Clock,
} from "lucide-react";
import { api } from "@/lib/api";
import { authHeaders } from "@/lib/auth";

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const fmtVersion = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

export default function ConsentLogPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/consent-records", {
        headers: authHeaders(),
      });
      setRows(Array.isArray(data) ? data : []);
      setError("");
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.user_ref, r.user_label, r.app, r.app_version, r.platform, r.ip]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [rows, query]);

  const exportCsv = () => {
    const headers = [
      "user_ref",
      "user_label",
      "accepted_at",
      "app",
      "app_version",
      "platform",
      "lang",
      "ip",
      "documents",
    ];
    const lines = [headers.join(",")];
    for (const r of filtered) {
      const docs = (r.documents || [])
        .map((d) => `${d.slug}@${fmtVersion(d.version)}`)
        .join(" | ");
      const vals = [
        r.user_ref,
        r.user_label,
        r.accepted_at,
        r.app,
        r.app_version,
        r.platform,
        r.lang,
        r.ip,
        docs,
      ].map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`);
      lines.push(vals.join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `consent-records-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="consent-log-panel" className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-bold text-[#0E2A36] flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-[#1F4E5F]" /> Consent Records
          </h2>
          <p className="text-[13px] text-[#3D5560] mt-0.5">
            Acceptances captured from the companion app, with the document
            version and app version accepted.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="consent-refresh"
            onClick={load}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-semibold text-[#1F4E5F] bg-[#E1EEEA] hover:bg-[#D3E6E0]"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          <button
            data-testid="consent-export"
            onClick={exportCsv}
            disabled={!filtered.length}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-semibold text-white bg-[#1F4E5F] hover:bg-[#163B49] disabled:opacity-50"
          >
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>
      </div>

      <div className="relative max-w-sm">
        <Search className="w-4 h-4 text-[#8AA0A8] absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          data-testid="consent-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search user, app version, platform, IP…"
          className="w-full pl-9 pr-3 py-2 rounded-full border border-[#C9DDD6] text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1F4E5F]/20"
        />
      </div>

      <div className="bg-white border border-[#C9DDD6] rounded-2xl overflow-hidden">
        {loading ? (
          <div className="py-16 flex items-center justify-center text-[#6A8290] text-sm">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : error ? (
          <div className="py-12 text-center text-[#C84444] text-sm">{error}</div>
        ) : filtered.length === 0 ? (
          <div
            data-testid="consent-empty"
            className="py-16 text-center text-[#6A8290] text-sm"
          >
            No consent records yet. They appear here once users accept in the app.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-[#6A8290] border-b border-[#EAF1EE]">
                <th className="px-4 py-3 font-semibold">User</th>
                <th className="px-4 py-3 font-semibold">Accepted</th>
                <th className="px-4 py-3 font-semibold">App ver.</th>
                <th className="px-4 py-3 font-semibold">Platform</th>
                <th className="px-4 py-3 font-semibold">Lang</th>
                <th className="px-4 py-3 font-semibold w-8"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const isOpen = expanded === r.id;
                return (
                  <Fragment key={r.id}>
                    <tr
                      data-testid={`consent-row-${r.id}`}
                      onClick={() => setExpanded(isOpen ? null : r.id)}
                      className="border-b border-[#F2F7F5] hover:bg-[#F7FBFA] cursor-pointer"
                    >
                      <td className="px-4 py-3">
                        <p className="font-semibold text-[#0E2A36]">
                          {r.user_label || r.user_ref}
                        </p>
                        {r.user_label && (
                          <p className="text-[11px] text-[#6A8290]">{r.user_ref}</p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-[#36505A] whitespace-nowrap">
                        {fmtDateTime(r.accepted_at)}
                      </td>
                      <td className="px-4 py-3 text-[#36505A]">
                        {r.app_version || "—"}
                      </td>
                      <td className="px-4 py-3 text-[#36505A]">{r.platform || "—"}</td>
                      <td className="px-4 py-3 text-[#36505A] uppercase">{r.lang}</td>
                      <td className="px-4 py-3">
                        <ChevronDown
                          className={`w-4 h-4 text-[#6A8290] transition-transform ${
                            isOpen ? "rotate-180" : ""
                          }`}
                        />
                      </td>
                    </tr>
                    {isOpen && (
                      <tr
                        data-testid={`consent-detail-${r.id}`}
                        className="bg-[#F7FBFA]"
                      >
                        <td colSpan={6} className="px-4 py-4">
                          <div className="grid sm:grid-cols-2 gap-4">
                            <div>
                              <p className="text-[11px] uppercase tracking-wider text-[#6A8290] mb-2 font-semibold">
                                Documents accepted
                              </p>
                              <ul className="space-y-1.5">
                                {(r.documents || []).map((d) => (
                                  <li
                                    key={d.slug}
                                    className="flex items-center justify-between gap-3 text-[13px] bg-white border border-[#E1EEEA] rounded-lg px-3 py-1.5"
                                  >
                                    <span className="text-[#0E2A36] font-medium">
                                      {d.title || d.slug}
                                    </span>
                                    <span className="text-[#6A8290] flex items-center gap-1">
                                      <Clock className="w-3 h-3" />
                                      {fmtVersion(d.version)}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                            <div className="text-[13px] text-[#36505A] space-y-1.5">
                              <p className="flex items-center gap-2">
                                <Smartphone className="w-3.5 h-3.5 text-[#6A8290]" />
                                <span className="text-[#6A8290]">App:</span>{" "}
                                {r.app || "—"} · {r.app_version || "—"}
                              </p>
                              <p>
                                <span className="text-[#6A8290]">Platform:</span>{" "}
                                {r.platform || "—"}
                              </p>
                              <p>
                                <span className="text-[#6A8290]">IP:</span>{" "}
                                {r.ip || "—"}
                              </p>
                              <p className="break-all">
                                <span className="text-[#6A8290]">User agent:</span>{" "}
                                {r.user_agent || "—"}
                              </p>
                              <p>
                                <span className="text-[#6A8290]">Record ID:</span>{" "}
                                <span className="font-mono text-[11px]">{r.id}</span>
                              </p>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <p className="text-[12px] text-[#6A8290]">
        Showing {filtered.length} of {rows.length} record
        {rows.length === 1 ? "" : "s"}. Consent records are immutable.
      </p>
    </div>
  );
}
